import React from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacingSystem from '../../spacing';

// Card variants
export type CardVariant = 'default' | 'outlined' | 'elevated';

// Card sizes
export type CardSize = 'small' | 'medium' | 'large';

// Card props
export interface CardProps {
  /**
   * The variant of the card
   * @default 'default'
   */
  variant?: CardVariant;
  
  /**
   * The size of the card
   * @default 'medium'
   */
  size?: CardSize;
  
  /**
   * Whether the card should take the full width of its container
   * @default false
   */
  fullWidth?: boolean;
  
  /**
   * Whether the card should have hover effects
   * @default false
   */
  interactive?: boolean;
  
  /**
   * The content of the card
   */
  children: React.ReactNode;
  
  /**
   * Optional click handler
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  
  /**
   * Additional class name
   */
  className?: string;
}

// Variant styles
const getVariantStyles = (variant: CardVariant) => {
  switch (variant) {
    case 'default':
      return css`
        background-color: white;
        border: none;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      `;
    case 'outlined':
      return css`
        background-color: white;
        border: 1px solid ${colors.neutral[200]};
        box-shadow: none;
      `;
    case 'elevated':
      return css`
        background-color: white;
        border: none;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
      `;
    default:
      return '';
  }
};

// Size styles
const getSizeStyles = (size: CardSize) => {
  switch (size) {
    case 'small':
      return css`
        padding: ${spacingSystem.spacing[12]};
      `;
    case 'medium':
      return css`
        padding: ${spacingSystem.spacing[16]};
      `;
    case 'large':
      return css`
        padding: ${spacingSystem.spacing[24]};
      `;
    default:
      return '';
  }
};

// Interactive styles
const getInteractiveStyles = (interactive: boolean, variant: CardVariant) => {
  if (!interactive) return '';
  
  switch (variant) {
    case 'default':
      return css`
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        
        &:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
      `;
    case 'outlined':
      return css`
        cursor: pointer;
        transition: border-color 0.2s ease, transform 0.2s ease;
        
        &:hover {
          border-color: ${colors.neutral[300]};
          transform: translateY(-2px);
        }
        
        &:active {
          transform: translateY(0);
        }
      `;
    case 'elevated':
      return css`
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.06);
        }
        
        &:active {
          transform: translateY(0);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
        }
      `;
    default:
      return '';
  }
};

// Define StyledCardProps with transient props
interface StyledCardProps {
  $variant?: CardVariant;
  $size?: CardSize;
  $fullWidth?: boolean;
  $interactive?: boolean;
}

// Styled card component
const StyledCard = styled.div<StyledCardProps>`
  /* Base styles */
  display: block;
  border-radius: 8px;
  font-family: ${typography.fontFamily.primary};
  color: ${colors.neutral[900]};
  
  /* Variant styles */
  ${props => getVariantStyles(props.$variant || 'default')}
  
  /* Size styles */
  ${props => getSizeStyles(props.$size || 'medium')}
  
  /* Full width */
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  
  /* Interactive styles */
  ${props => getInteractiveStyles(Boolean(props.$interactive), props.$variant || 'default')}
`;

/**
 * Card component for containing content
 */
// Define the Card component type with subcomponents
interface CardComponent extends React.ForwardRefExoticComponent<CardProps & React.RefAttributes<HTMLDivElement>> {
  Header: typeof CardHeader;
  Content: typeof CardContent;
  Footer: typeof CardFooter;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      size = 'medium',
      fullWidth = false,
      interactive = false,
      children,
      onClick,
      className,
      ...rest
    },
    ref
  ) => {
    return (
      <StyledCard
        ref={ref}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $interactive={interactive}
        onClick={interactive ? onClick : undefined}
        className={className}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        {...rest}
      >
        {children}
      </StyledCard>
    );
  }
);

Card.displayName = 'Card';

// Card subcomponents
interface CardHeaderProps {
  size?: CardSize;
}

const CardHeader = styled.div<CardHeaderProps>`
  padding: ${spacingSystem.spacing[8]} ${spacingSystem.spacing[16]};
  border-bottom: 1px solid ${colors.neutral[200]};
  font-weight: ${typography.fontWeight.medium};
  font-size: ${typography.fontSize.lg};
  margin: -${props => {
    switch (props.size) {
      case 'small': return spacingSystem.spacing[12];
      case 'large': return spacingSystem.spacing[24];
      default: return spacingSystem.spacing[16];
    }
  }};
  margin-bottom: ${spacingSystem.spacing[16]};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const CardContent = styled.div`
  /* Content has padding from the parent Card */
`;

interface CardFooterProps {
  size?: CardSize;
}

const CardFooter = styled.div<CardFooterProps>`
  padding: ${spacingSystem.spacing[8]} ${spacingSystem.spacing[16]};
  border-top: 1px solid ${colors.neutral[200]};
  margin: -${props => {
    switch (props.size) {
      case 'small': return spacingSystem.spacing[12];
      case 'large': return spacingSystem.spacing[24];
      default: return spacingSystem.spacing[16];
    }
  }};
  margin-top: ${spacingSystem.spacing[16]};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

// Export subcomponents
(Card as CardComponent).Header = CardHeader;
(Card as CardComponent).Content = CardContent;
(Card as CardComponent).Footer = CardFooter;

export default Card as CardComponent;